import {
  executeEVMSession,
  executeSolanaSession,
  InsufficientPaymentAmountError,
  MFACodeRequiredError,
  payWithCoinbaseApp,
  Session,
  waitForSession,
} from "@paywithglide/glide-js";
import TransactionCheckoutHeader from "../components/TransactionCheckoutHeader";
import { useGlideDepositConfig } from "../lib/useDepositConfig";
import { formatDisplayAmount } from "../lib/formatDisplayAmount";
import { LogoWithBadge } from "../components/LogoWithBadge";
import PrimaryButton from "../components/PrimaryButton";
import ModalBackButton from "../components/ModalBackButton";
import SessionSummary from "../components/SessionSummary";
import { MousePointerClick, Loader2, Clock, CircleCheck } from "lucide-react";
import Alert from "../components/Alert";
import Warning from "../components/Warning";
import { useState, useRef, useMemo } from "react";
import { getConnectedWallet } from "../lib/getConnectedWallet";
import { getTokenConversionMessage } from "../lib/getTokenConversionMessage";
import { logger } from "../lib/logger";
import { glideLocalStorage } from "../lib/glideLocalStorage";
import { useDeviceContext } from "../lib/useDeviceContext";
import { GlideRoute } from "../lib/listGlideRoutes";
import { isOverpaymentAboveThreshold } from "../lib/overpayment";

type Props = {
  session: Session;
  routes: GlideRoute[];
  coinbaseAccessToken?: string;
  onBack: () => void;
  onSuccess: (session: Session) => void;
  onMFA: () => void;
  onNavigateToGasRefillExplainer?: () => void;
  onInsufficientPaymentAmount?: (session: Session) => void;
  onOverpaymentRefund?: (session: Session) => void;
  onRoutePriorityChange?: (priority: "speed" | "cost") => Promise<Session>;
  onChangePaymentToken?: () => void;
  onDeferredTransaction: () => void;
};

export function ReviewSession({
  session: initialSession,
  routes,
  coinbaseAccessToken,
  onBack,
  onSuccess,
  onMFA,
  onNavigateToGasRefillExplainer,
  onInsufficientPaymentAmount,
  onOverpaymentRefund,
  onRoutePriorityChange,
  onChangePaymentToken,
  onDeferredTransaction,
}: Props) {
  const { glideConfig, ...depositConfig } = useGlideDepositConfig();
  const { isMobile } = useDeviceContext();

  const [session, setSession] = useState(initialSession);
  const [isExecuting, setIsExecuting] = useState(false);
  const [didCompletePayment, setDidCompletePayment] = useState(false);
  const didTriggerOverpaymentRef = useRef(false);
  const [isChangingRoute, setIsChangingRoute] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const tokenPriceUSD = useMemo(() => {
    const paymentAmount = Number(session.paymentAmount);
    const paymentAmountUSD = Number(session.paymentAmountUSD);
    if (paymentAmount > 0 && paymentAmountUSD > 0) {
      return paymentAmountUSD / paymentAmount;
    }
    return undefined;
  }, [session.paymentAmount, session.paymentAmountUSD]);

  const sponsoredTokenPriceUSD = useMemo(() => {
    const sponsoredAmount = Number(session.sponsoredTransactionAmount);
    const sponsoredAmountUSD = Number(session.sponsoredTransactionAmountUSD);
    if (sponsoredAmount > 0 && sponsoredAmountUSD > 0) {
      return sponsoredAmountUSD / sponsoredAmount;
    }
    return undefined;
  }, [
    session.sponsoredTransactionAmount,
    session.sponsoredTransactionAmountUSD,
  ]);

  const filteredRoutes = routes.filter(
    (r) =>
      Number(r.maxAmount) > Number(session.sponsoredTransactionAmountUSD) ||
      Number(r.maxAmount) === -1,
  );

  // Determine default route based on session.etaInSeconds
  // If etaInSeconds <= 10, it's the fastest route (speed), otherwise it's the cheapest (cost)
  const isFastestRoute = session.etaInSeconds <= 10;
  const defaultRoute = filteredRoutes.find((r) =>
    isFastestRoute ? r.priority === "speed" : r.priority === "cost",
  );
  const [selectedRouteId, setSelectedRouteId] = useState<string>(
    defaultRoute?.id || filteredRoutes[0]?.id || "",
  );

  const payOnCoinbase = async () => {
    try {
      if (!coinbaseAccessToken) {
        throw new Error("Coinbase access token is required");
      }

      await payWithCoinbaseApp(glideConfig, {
        sessionId: session.sessionId,
        accessToken: coinbaseAccessToken,
      });

      await waitForSession(glideConfig, {
        sessionId: session.sessionId,
        onUpdate: (updatedSession) => {
          setSession(updatedSession);

          // Call success when payment is confirmed
          if (updatedSession.paymentStatus === "paid") {
            onSuccess(updatedSession);
          }

          // Detect overpayment - only in pay mode, only after funds arrive
          const isFunded =
            updatedSession.paymentStatus === "waiting_confirmations" ||
            updatedSession.paymentStatus === "paid";

          if (
            depositConfig.mode === "pay" &&
            isFunded &&
            onOverpaymentRefund &&
            updatedSession.paymentAmountUSD &&
            updatedSession.actualPaymentAmountUSD &&
            !didTriggerOverpaymentRef.current
          ) {
            const requiredAmount = Number(updatedSession.paymentAmountUSD);
            const paidAmount = Number(updatedSession.actualPaymentAmountUSD);

            if (isOverpaymentAboveThreshold(requiredAmount, paidAmount)) {
              const excessAmount = paidAmount - requiredAmount;
              console.log(
                `Overpayment detected: Required ${requiredAmount}, Paid ${paidAmount}, Excess ${excessAmount}`,
              );
              didTriggerOverpaymentRef.current = true;
              onOverpaymentRefund(updatedSession);
            }
          }
        },
      });
    } catch (error) {
      if (error instanceof MFACodeRequiredError) {
        onMFA();
      } else {
        throw error;
      }
    }
  };

  const payWithWallet = async () => {
    // Determine ecosystem from the session's payment chain, not from localStorage
    const isSolanaPayment = session.paymentChainId.includes("solana");

    const {
      chainId,
      switchChainAsync,
      sendTransactionAsync,
      signTypedDataAsync,
      signAndSendSolanaTransaction,
    } = await getConnectedWallet({
      // Pass ecosystem hint for Solana payments, let localStorage determine useExternalWallet
      externalWalletEcosystem: isSolanaPayment ? "solana" : "evm",
    });

    if (isExecuting || !chainId) {
      throw new Error("Invalid state");
    }

    try {
      if (session.paymentChainId.includes("solana")) {
        await executeSolanaSession(glideConfig, {
          session,
          signAndSendTransaction: signAndSendSolanaTransaction,
          onCompletePayment: () => setDidCompletePayment(true),
          onSessionUpdate: (updatedSession) => {
            setSession(updatedSession);

            // Call success when payment is confirmed
            if (updatedSession.paymentStatus === "paid") {
              onSuccess(updatedSession);
            }

            // Detect overpayment - only in pay mode, only after funds arrive
            const isFunded =
              updatedSession.paymentStatus === "waiting_confirmations" ||
              updatedSession.paymentStatus === "paid";

            if (
              depositConfig.mode === "pay" &&
              isFunded &&
              onOverpaymentRefund &&
              updatedSession.paymentAmountUSD &&
              updatedSession.actualPaymentAmountUSD &&
              !didTriggerOverpaymentRef.current
            ) {
              const requiredAmount = Number(updatedSession.paymentAmountUSD);
              const paidAmount = Number(updatedSession.actualPaymentAmountUSD);

              if (isOverpaymentAboveThreshold(requiredAmount, paidAmount)) {
                const excessAmount = paidAmount - requiredAmount;
                console.log(
                  `Overpayment detected: Required ${requiredAmount}, Paid ${paidAmount}, Excess ${excessAmount}`,
                );
                didTriggerOverpaymentRef.current = true;
                onOverpaymentRefund(updatedSession);
              }
            }

            window.parent?.postMessage(
              { type: "sessionUpdate", session: updatedSession },
              "*",
            );
          },
        });
      } else {
        const result = await executeEVMSession(glideConfig, {
          session,
          currentChainId: chainId,
          switchChainAsync,
          sendTransactionAsync,
          signTypedDataAsync,

          onCompletePayment: () => setDidCompletePayment(true),
          onSessionUpdate: (updatedSession) => {
            setSession(updatedSession);

            // Call success when payment is confirmed
            if (updatedSession.paymentStatus === "paid") {
              onSuccess(updatedSession);
            }

            // Detect overpayment - only in pay mode, only after funds arrive
            const isFunded =
              updatedSession.paymentStatus === "waiting_confirmations" ||
              updatedSession.paymentStatus === "paid";

            if (
              depositConfig.mode === "pay" &&
              isFunded &&
              onOverpaymentRefund &&
              updatedSession.paymentAmountUSD &&
              updatedSession.actualPaymentAmountUSD &&
              !didTriggerOverpaymentRef.current
            ) {
              const requiredAmount = Number(updatedSession.paymentAmountUSD);
              const paidAmount = Number(updatedSession.actualPaymentAmountUSD);

              if (isOverpaymentAboveThreshold(requiredAmount, paidAmount)) {
                const excessAmount = paidAmount - requiredAmount;
                console.log(
                  `Overpayment detected: Required ${requiredAmount}, Paid ${paidAmount}, Excess ${excessAmount}`,
                );
                didTriggerOverpaymentRef.current = true;
                onOverpaymentRefund(updatedSession);
              }
            }

            window.parent?.postMessage(
              { type: "sessionUpdate", session: updatedSession },
              "*",
            );
          },
        });

        if (result && "deferred" in result) {
          onDeferredTransaction();
        }
      }
    } catch (error) {
      if (error instanceof InsufficientPaymentAmountError) {
        if (depositConfig.mode === "pay" && onInsufficientPaymentAmount) {
          onInsufficientPaymentAmount(session);
        } else {
          onSuccess(session);
        }
        return;
      }
      throw error;
    }
  };

  const onExecuteSession = async () => {
    try {
      setIsExecuting(true);
      setDidCompletePayment(false);

      // @ts-expect-error: cex:coinbase isn't a valid CAIP2 yet
      if (session.paymentChainId === "cex:coinbase") {
        return await payOnCoinbase();
      }

      const recentConnectorId = await glideLocalStorage.getItem(
        "glide.wagmi.recentConnectorId",
      );
      logger.info(
        `Recent connector id ${recentConnectorId || "unknown"} ${
          recentConnectorId === "walletConnect"
        }`,
      );
      if (recentConnectorId === '"walletConnect"') {
        try {
          logger.info("Getting WalletConnect sessions");
          const rawSessions = await glideLocalStorage.getItem(
            "wc@2:client:0.3:glide.wc//session",
          );
          if (!rawSessions) {
            logger.info("WalletConnect sessions not found");
            // Continue with payment flow even if WalletConnect sessions not found
          } else {
            logger.info("Parsing WalletConnect sessions");
            const sessions = JSON.parse(rawSessions);
            if (sessions.length === 0) {
              logger.info("WalletConnect sessions is empty");
              // Continue with payment flow even if sessions array is empty
            } else {
              const latestSession = sessions.reduce(
                (
                  maxSession: { expiry: number },
                  currentSession: { expiry: number },
                ) => {
                  return currentSession.expiry > maxSession.expiry
                    ? currentSession
                    : maxSession;
                },
                sessions[0],
              );

              logger.info(
                `Found session with deep link ${latestSession.peer.metadata.redirect.native}`,
              );

              let timeout: NodeJS.Timeout | undefined = setTimeout(() => {
                timeout = undefined;

                if (isMobile) {
                  logger.info(
                    `Opening with deep link.. ${latestSession.peer.metadata.redirect.native}`,
                  );
                  /*
                   * WalletConnect, by default, won't always open the deep link on its own.
                   * Instead, it sends a notification via the wallet prompting the user to open
                   * the app. This isn't always reliable, so we'll try to open the deep link
                   * directly.
                   */
                  window.open(latestSession.peer.metadata.redirect.native);
                }
              }, 1500);

              document.addEventListener("visibilitychange", () => {
                if (document.hidden && timeout) {
                  logger.info(`Fallback deep link timeout cleared`);
                  clearTimeout(timeout);
                }
              });
            }
          }
        } catch (error) {
          logger.error(`Error parsing WalletConnect sessions: ${error}`);
        }
      }

      return await payWithWallet();
    } catch (error) {
      logger.error(
        error instanceof Error ? error.message : JSON.stringify(error),
      );

      let errorMessage = "Transaction failed. Please try again.";
      if (error instanceof Error) {
        // Extract only the first line of the error message
        // This removes viem's detailed error info (Request Arguments, Details, Version, etc.)
        errorMessage = error.message.split("\n")[0].trim();
      } else if (typeof error === "string") {
        // Handle string errors from parent wallet provider via postMessage
        errorMessage = error.split("\n")[0].trim();
      }

      setError(errorMessage);
      setIsExecuting(false);
    }
  };

  const tokenConversionMessage = getTokenConversionMessage({
    from: {
      tokenSymbol: session.paymentCurrencySymbol,
      chainId: session.paymentChainId,
      chainName: session.paymentChainName,
    },
    to: {
      tokenSymbol: session.sponsoredTransactionCurrencySymbol,
      chainId: session.sponsoredTransactionChainId,
      chainName: session.sponsoredTransactionChainName,
    },
  });

  return (
    <>
      <ModalBackButton onClick={() => onBack()} />
      <div>
        {depositConfig.mode === "deposit" ? (
          <TransactionCheckoutHeader title="Review deposit" />
        ) : depositConfig.mode === "withdraw" ? (
          <TransactionCheckoutHeader title="Review withdrawal" />
        ) : (
          <TransactionCheckoutHeader title="Review" />
        )}

        {depositConfig.mode === "deposit" ||
        depositConfig.mode === "withdraw" ? (
          !depositConfig.hideSettlementCopy ? (
            <div>
              <div className="text-xs text-[--color-text-secondary]">
                You'll receive
              </div>
              <div className="flex items-center justify-between">
                <div className="mt-1 text-4xl text-[--color-text-primary]">
                  {formatDisplayAmount(
                    session.sponsoredTransactionAmount,
                    sponsoredTokenPriceUSD,
                  )}{" "}
                  {session.sponsoredTransactionCurrencySymbol}
                </div>
                <div>
                  <LogoWithBadge
                    logoUrl={session.sponsoredTransactionCurrencyLogoUrl}
                    badgeUrl={session.sponsoredTransactionChainLogoUrl}
                    size="lg"
                  />
                </div>
              </div>
              <div className="text-sm text-[--color-text-secondary]">
                $
                {Number(session.sponsoredTransactionAmountUSD).toLocaleString(
                  undefined,
                  {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  },
                )}
              </div>
            </div>
          ) : null
        ) : null}

        {depositConfig.mode === "pay" ||
        depositConfig.mode === "call" ||
        (depositConfig.mode === "deposit" &&
          !depositConfig.hideSettlementCopy) ? (
          <div className="mt-6">
            <div className="text-xs text-[--color-text-secondary]">
              {depositConfig.mode === "deposit" &&
              depositConfig.hideSettlementCopy
                ? "You're depositing"
                : "You're paying"}
            </div>
            <div className="flex items-center justify-between">
              <div className="mt-1 text-4xl text-[--color-text-primary]">
                {formatDisplayAmount(session.paymentAmount, tokenPriceUSD)}{" "}
                {session.paymentCurrencySymbol}
              </div>
              <div>
                <LogoWithBadge
                  logoUrl={session.paymentCurrencyLogoUrl}
                  badgeUrl={session.paymentChainLogoUrl}
                  size="lg"
                />
              </div>
            </div>
            <div className="text-sm text-[--color-text-secondary] flex items-center gap-1.5">
              <span>
                $
                {Number(session.paymentAmountUSD).toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </span>
              {onChangePaymentToken &&
                (depositConfig.mode === "pay" ||
                  depositConfig.mode === "call") && (
                  <>
                    <span>•</span>
                    <button
                      onClick={onChangePaymentToken}
                      className="hover:underline"
                    >
                      Switch Token
                    </button>
                  </>
                )}
            </div>
          </div>
        ) : null}

        {tokenConversionMessage &&
        (depositConfig.mode === "deposit" ||
          depositConfig.mode === "withdraw") &&
        !depositConfig.hideSettlementCopy ? (
          <div className="mt-2 text-xs text-[--color-text-secondary]">
            {tokenConversionMessage}
          </div>
        ) : null}

        {/* HyperCore account activation fee notice */}
        {session.sponsoredTransactionChainId === "hypercore:mainnet" ? (
          <Warning className="mt-4">
            <strong>First-time HyperCore deposit:</strong> If you've never
            deposited to this Hyperliquid account before, a one-time fee of 1
            USDC will be deducted.
          </Warning>
        ) : null}

        <hr className="my-4 border-0.5 border-[--color-background-secondary]" />

        {filteredRoutes.length === 2 && !isExecuting && (
          <div className="mb-4">
            <div className="flex justify-between items-center h-7">
              <div className="text-xs text-[--color-text-secondary]">Mode</div>
              <div className="flex items-center gap-2">
                {isChangingRoute && (
                  <Loader2 className="h-4 w-4 animate-spin text-[--color-text-secondary]" />
                )}
                <div className="flex items-center gap-1 bg-[--button-secondary-background] rounded-md p-0.5">
                  {filteredRoutes
                    .sort((a, b) => {
                      // Speed (Fastest) always appears first
                      if (a.priority === "speed" && b.priority === "cost")
                        return -1;
                      if (a.priority === "cost" && b.priority === "speed")
                        return 1;
                      return 0;
                    })
                    .map((route) => (
                      <button
                        key={route.id}
                        type="button"
                        disabled={isChangingRoute}
                        onClick={async () => {
                          setSelectedRouteId(route.id);
                          // Find the route priority and notify parent
                          if (onRoutePriorityChange) {
                            setIsChangingRoute(true);
                            try {
                              const updatedSession =
                                await onRoutePriorityChange(route.priority);
                              setSession(updatedSession);
                            } finally {
                              setIsChangingRoute(false);
                            }
                          }
                        }}
                        className={`px-2 py-1 rounded text-xs font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                          selectedRouteId === route.id
                            ? "bg-[--color-modal-background] text-[--color-text-primary] shadow-sm"
                            : "text-[--color-text-secondary] hover:text-[--color-text-primary]"
                        }`}
                      >
                        {route.priority === "speed" ? "Fastest" : "Cheapest"}
                      </button>
                    ))}
                </div>
              </div>
            </div>
          </div>
        )}

        <div>
          {isExecuting &&
          !didCompletePayment &&
          !session.paymentChainId.startsWith("cex:") ? (
            <Alert
              id="continue_in_wallet"
              icon={
                <MousePointerClick strokeWidth={2.25} className="w-4 h-4" />
              }
              title="Continue in wallet"
              subtitle={`Follow the prompts in your wallet to complete the ${
                depositConfig.mode === "buy" ||
                depositConfig.mode === "pay" ||
                depositConfig.mode === "call"
                  ? "payment"
                  : "transfer"
              }.`}
            />
          ) : isExecuting &&
            didCompletePayment &&
            session.paymentStatus === "unpaid" ? (
            <Alert
              id="waiting_for_payment"
              icon={<Clock strokeWidth={2.25} className="w-4 h-4" />}
              title={`Waiting for ${
                depositConfig.mode === "buy" ||
                depositConfig.mode === "pay" ||
                depositConfig.mode === "call"
                  ? "payment"
                  : "transfer"
              }`}
              subtitle={`We're waiting for your ${
                depositConfig.mode === "pay" || depositConfig.mode === "call"
                  ? "payment"
                  : "transfer"
              }. This takes about 15 seconds.`}
            />
          ) : isExecuting &&
            session.paymentStatus === "waiting_confirmations" ? (
            <Alert
              id="waiting_confirmations"
              icon={<CircleCheck strokeWidth={2.25} className="w-4 h-4" />}
              title={`${
                depositConfig.mode === "buy" ||
                depositConfig.mode === "pay" ||
                depositConfig.mode === "call"
                  ? "Payment"
                  : "Transfer"
              } received`}
              subtitle={`Confirming your ${
                depositConfig.mode === "buy" ||
                depositConfig.mode === "pay" ||
                depositConfig.mode === "call"
                  ? "payment"
                  : "transfer"
              }. This takes a few seconds.`}
            />
          ) : null}

          {!isExecuting && (
            <>
              {error && <Warning className="mb-4">{error}</Warning>}

              <PrimaryButton
                className="mt-4"
                onClick={() => {
                  setError(null);
                  onExecuteSession();
                }}
                loading={isExecuting}
                disabled={isChangingRoute}
              >
                {depositConfig.mode === "deposit"
                  ? "Deposit now"
                  : depositConfig.mode === "withdraw"
                    ? "Withdraw now"
                    : depositConfig.mode === "pay" ||
                        depositConfig.mode === "call"
                      ? "Pay now"
                      : depositConfig.mode === "buy"
                        ? "Buy now"
                        : ""}
              </PrimaryButton>

              {depositConfig.mode === "buy" ? (
                <div className="mt-2 text-center">
                  <p className="text-xs text-[--color-text-secondary] italic">
                    Additional fees may apply
                  </p>
                </div>
              ) : null}
            </>
          )}
        </div>

        <SessionSummary
          session={session}
          recipient={depositConfig.recipient}
          showRecipient={
            !(depositConfig.mode === "pay" || depositConfig.mode === "call") &&
            !depositConfig.hideSettlementCopy
          }
          showFees={depositConfig.mode !== "pay"}
          onNavigateToGasRefillExplainer={onNavigateToGasRefillExplainer}
          className="mt-1"
        />
      </div>
    </>
  );
}
